"use client";

import Link from "next/link";
import { PhoneCall, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/analytics";
import { MvpHeroVisual } from "./MvpHeroVisual";

interface AboveTheFoldMvpProps {
  isItalian: boolean;
  bookingLink: string;
}

export function AboveTheFoldMvp({
  isItalian,
  bookingLink,
}: AboveTheFoldMvpProps) {
  return (
    <section className="section-padding pt-8 md:pt-20 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
      <div className="absolute inset-0 pattern-dots" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container-wide relative">
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-center">
          <div className="max-w-2xl text-left animate-fade-up">
          <span className="font-mono text-label text-primary mb-4 block">
            MVP Custom
          </span>
          <h1 className="page-hero-title mb-6">
            {isItalian ? (
              <>
                Un prodotto vero da testare, in{" "}
                <span className="text-shine">settimane</span>
              </>
            ) : (
              <>
                A real product to test, in{" "}
                <span className="text-shine">weeks</span>
              </>
            )}
          </h1>
          <p className="text-body-lg text-muted-foreground mb-6 leading-relaxed max-w-xl">
            {isItalian ? (
              <>
                Trasformo la tua idea in un MVP{" "}
                <span className="font-medium text-foreground">
                  online e pronto da mettere in mano a utenti veri
                </span>
                . SaaS, web app o PWA, in base a quello che serve per partire.
              </>
            ) : (
              <>
                I turn your idea into an MVP that's{" "}
                <span className="font-medium text-foreground">
                  online and ready to put in the hands of real users
                </span>
                . SaaS, web app or PWA, whatever fits your launch.
              </>
            )}
          </p>
          <p className="text-body font-medium mb-8 max-w-xl">
            {isItalian ? (
              <>
                Da <span className="text-primary font-semibold">4.800€</span> il
                primo mese.
              </>
            ) : (
              <>
                From <span className="text-primary font-semibold">€4,800</span> the
                first month.
              </>
            )}
          </p>
          <div className="flex flex-col sm:flex-row justify-start gap-4">
            <Button variant="hero" size="xl" className="w-full sm:w-auto" asChild>
              <Link
                href={bookingLink}
                onClick={() =>
                  trackEvent("book_call_click", {
                    tool: "tidycal",
                    location: "hero",
                  })
                }
              >
                <span className="sm:hidden">
                  {isItalian ? "Prenota call" : "Book call"}
                </span>
                <span className="hidden sm:inline">
                  {isItalian
                    ? "Prenota una call iniziale"
                    : "Book a discovery call"}
                </span>
                <PhoneCall className="w-4 h-4" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="rotating-border w-full sm:w-auto"
              asChild
            >
              <a
                href="#mvp-processo"
                onClick={() =>
                  trackEvent("cta_click", {
                    cta_text: isItalian ? "Come funziona l'MVP" : "How the MVP works",
                    cta_section: "mvp_hero",
                  })
                }
              >
                {isItalian ? "Come funziona" : "How it works"}
                <ArrowDown className="w-4 h-4" />
              </a>
            </Button>
          </div>
          </div>

          {/* Hero visual - desktop only */}
          <div className="flex justify-center items-center">
            <MvpHeroVisual isItalian={isItalian} />
          </div>

        </div>
      </div>
    </section>
  );
}
